import { cs } from '../../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../ui/select';
import { CreditCard, Banknote, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { invoiceKey } from './utils.jsx';

const OFFLINE_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'other', label: 'Other' },
];

const openBalanceOf = (inv) => {
  const v = inv.open_balance ?? inv.remaining_due ?? inv.balance_due ?? inv.amount ?? 0;
  return Math.max(0, Number(v) || 0);
};

/**
 * Charge (or record payment for) several of a parent's unpaid invoices at once.
 * Each row's amount is editable — enter less than the balance to take a PARTIAL
 * payment (the invoice drops to "Partially paid" with the remainder outstanding).
 * Method is per-charge: a real charge on the card on file, or an offline record.
 */
export default function BulkChargeDialog({ open, onOpenChange, parentId, invoices = [], onDone }) {
  const [method, setMethod] = useState('saved_card');
  const [offlineMethod, setOfflineMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [amounts, setAmounts] = useState({}); // invoiceKey -> string
  const [pm, setPm] = useState({ has_payment_method: false, payment_methods: [] });
  const [pmId, setPmId] = useState('');
  const [pmLoading, setPmLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Seed editable amounts with each invoice's open balance when opened.
  useEffect(() => {
    if (!open) return;
    const seed = {};
    invoices.forEach((inv) => { seed[invoiceKey(inv)] = openBalanceOf(inv).toFixed(2); });
    setAmounts(seed);
    setNote('');
  }, [open, invoices]);

  // Load the parent's saved payment methods to drive the card option.
  useEffect(() => {
    if (!open || !parentId) return;
    let cancelled = false;
    (async () => {
      setPmLoading(true);
      try {
        const res = await api.get(`/admin/parent/${parentId}/payment-methods`);
        if (cancelled) return;
        const data = res.data || {};
        setPm(data);
        if (data.payment_methods?.length) setPmId(data.payment_methods[0].id);
        if (!data.has_payment_method) setMethod('offline');
      } catch {
        if (!cancelled) { setPm({ has_payment_method: false, payment_methods: [] }); setMethod('offline'); }
      } finally {
        if (!cancelled) setPmLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, parentId]);

  const total = useMemo(
    () => invoices.reduce((s, inv) => s + (Number(amounts[invoiceKey(inv)]) || 0), 0),
    [invoices, amounts],
  );

  const submit = async () => {
    const items = invoices.map((inv) => ({
      entity_type: inv.type || inv.invoice_type || 'manual',
      entity_id: inv.id,
      amount: Number(amounts[invoiceKey(inv)]) || 0,
    })).filter((it) => it.amount > 0);

    if (items.length === 0) { toast.error('Enter an amount greater than 0 for at least one invoice.'); return; }
    if (method === 'saved_card' && !pm.has_payment_method) {
      toast.error('This parent has no card on file. Choose offline, or add a card first.');
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post(`/parents/${parentId}/billing/charge-invoices`, {
        method,
        payment_method_id: method === 'saved_card' ? (pmId || undefined) : undefined,
        offline_method: method === 'offline' ? offlineMethod : undefined,
        note: note || undefined,
        items,
      });
      const paid = data.paid_count || 0;
      const partial = data.partial_count || 0;
      const failed = data.failed_count || 0;
      const parts = [];
      if (paid) parts.push(`${paid} paid`);
      if (partial) parts.push(`${partial} partially paid`);
      if (failed) parts.push(`${failed} failed`);
      const msg = `${cs()}${Number(data.charged_total || 0).toFixed(2)} processed · ${parts.join(' · ') || 'no changes'}`;
      if (failed > 0) {
        toast.warning(msg);
        (data.results || []).filter((r) => !r.ok).forEach((r) => toast.error(`${r.entity_type} ${r.entity_id}: ${r.error}`));
      } else {
        toast.success(msg);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to charge invoices');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)]" data-testid="bulk-charge-dialog">
        <DialogHeader>
          <DialogTitle>Charge {invoices.length} invoice{invoices.length === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Edit an amount to take a partial payment — the invoice stays open with the remaining balance overdue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* Per-invoice amounts */}
          <div className="space-y-2">
            {invoices.map((inv) => {
              const k = invoiceKey(inv);
              const bal = openBalanceOf(inv);
              return (
                <div key={k} className="flex items-center gap-3 border border-slate-200 rounded-md px-3 py-2" data-testid={`bulk-charge-row-${inv.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{inv.description || inv.type_label || inv.type}</p>
                    <p className="text-xs text-slate-500 capitalize">{String(inv.type || '').replace(/_/g, ' ')} · balance {cs()}{bal.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 text-sm">$</span>
                    <Input
                      type="number" min="0" step="0.01"
                      className="w-24 h-8"
                      value={amounts[k] ?? ''}
                      onChange={(e) => setAmounts((p) => ({ ...p, [k]: e.target.value }))}
                      data-testid={`bulk-charge-amount-${inv.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Method */}
          <div className="space-y-2">
            <Label>Payment method</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => pm.has_payment_method && setMethod('saved_card')}
                disabled={!pm.has_payment_method}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${method === 'saved_card' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200'} ${!pm.has_payment_method ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="bulk-charge-method-card"
              >
                <CreditCard className="h-4 w-4" /> Card on file
              </button>
              <button
                type="button"
                onClick={() => setMethod('offline')}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${method === 'offline' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200'}`}
                data-testid="bulk-charge-method-offline"
              >
                <Banknote className="h-4 w-4" /> Offline payment
              </button>
            </div>
            {pmLoading && <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Checking card on file…</p>}
            {!pmLoading && !pm.has_payment_method && (
              <p className="text-xs text-amber-600" data-testid="bulk-charge-no-card">No card on file — record an offline payment, or add a card first.</p>
            )}
          </div>

          {method === 'saved_card' && pm.payment_methods?.length > 1 && (
            <div className="space-y-1">
              <Label>Card</Label>
              <Select value={pmId} onValueChange={setPmId}>
                <SelectTrigger data-testid="bulk-charge-card-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pm.payment_methods.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {(m.brand || m.type || 'card')} •••• {m.last4 || m.last_four || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {method === 'offline' && (
            <div className="space-y-1">
              <Label>Offline method</Label>
              <Select value={offlineMethod} onValueChange={setOfflineMethod}>
                <SelectTrigger data-testid="bulk-charge-offline-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OFFLINE_METHODS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1">
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Check #1234" data-testid="bulk-charge-note" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing} data-testid="bulk-charge-cancel">Cancel</Button>
          <Button onClick={submit} disabled={processing || total <= 0} data-testid="bulk-charge-submit">
            {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing…</>
              : method === 'saved_card' ? `Charge ${cs()}${total.toFixed(2)}` : `Record ${cs()}${total.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
